import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { entityService } from '../services/api';
import { reportClientError } from '../utils/reportClientError';
import { useSerialQueries } from '../hooks/useSerialQueries';
import { WidgetStatus } from '../components/WidgetStatus';
import {
    brandInputFocus, brandOutlineBtn, brandPrimaryBtnMd, brandStatusWarning,
} from '../components/brandChrome';
import {
    CategoryIconTile,
    ChainBadge,
    CopyIconButton,
    IntelStatIcon,
    normalizeChainLabel,
    RowActionButton,
    SeverityBadge,
    SourceMark,
    type IntelStatTone,
} from '../components/intel';
import type { LucideIcon } from 'lucide-react';
import {
    Search, AlertTriangle, Building2, RefreshCw, Database,
    ChevronLeft, ChevronRight, ChevronDown, ChevronUp, X, ExternalLink, BadgeCheck,
    Skull, Shuffle, Ban, MapPin,
    SlidersHorizontal, RotateCcw, Copy,
    ShieldCheck, CircleAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { EVM_CHAINS_ORDERED } from '../utils/chainDetect';
import { SkeletonStat, SkeletonTable } from '../components/ui/Skeleton';

const PAGE_SIZE = 50;
const EVM_CHAINS = new Set<string>(EVM_CHAINS_ORDERED);

interface EntityAttribution {
    key?: string;
    value?: string;
    source?: string;
    evidence?: string;
    confidence?: number;
}

interface EntityRecord {
    address?: string;
    chain?: string;
    name?: string;
    category?: string;
    subcategory?: string;
    source?: string;
    risk_level?: string;
    country?: string;
    verified?: boolean;
    description?: string;
    confidence?: number;
    tags?: string[];
    node_labels?: string[];
    intelligence_attributions?: EntityAttribution[];
    metadata?: Record<string, unknown>;
    brand?: string;
    wallet_role?: string;
    last_active_at?: string | number;
    recent_tx_count?: number;
}

interface EntityStats {
    total?: number;
    scams?: number;
    exchanges?: number;
    sanctioned?: number;
    ransomware?: number;
    mixers?: number;
    india_linked?: number;
    source_count?: number;
    by_chain?: Record<string, number>;
}

interface ExchangeIntelligence {
    brand: string;
    address_count: number;
    chain_count: number;
    india_linked?: boolean;
    has_verified?: boolean;
}

interface EntityTransaction {
    tx_hash?: string;
    from_address?: string;
    to_address?: string;
    other_address_entity_name?: string;
    value?: number;
    historical_usd?: number;
    token_symbol?: string;
    timestamp?: string | number;
}

const addressesMatch = (left?: string, right?: string, chain?: string) => {
    if (!left || !right) return false;
    return EVM_CHAINS.has((chain || '').toUpperCase())
        ? left.toLowerCase() === right.toLowerCase()
        : left === right;
};

export const Entities: React.FC = () => {
    const navigate = useNavigate();
    const [entities, setEntities] = useState<EntityRecord[]>([]);
    const [totalCount, setTotalCount] = useState<number | null>(null);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [search, setSearch] = useState('');
    const [committedSearch, setCommittedSearch] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [filterSubcategory, setFilterSubcategory] = useState('all');
    const [filterSource, setFilterSource] = useState('all');
    const [filterRisk, setFilterRisk] = useState('all');
    const [filterCountry, setFilterCountry] = useState('all');
    const [filterRecentActivity, setFilterRecentActivity] = useState(false);
    const [regionTab, setRegionTab] = useState<'all' | 'india'>('all');
    const [entitiesError, setEntitiesError] = useState<string | null>(null);
    const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const abortRef = useRef<AbortController | null>(null);
    const requestIdRef = useRef(0);

    // Dashboard widgets — orchestrated with per-widget status so one slow call
    // doesn't cascade into 30s timeouts across the others.
    // Stats runs on its own schedule (auto-refresh every 60s). The lookup
    // lists (sources/types/subcategories/countries) refresh only on page load
    // + explicit refresh — they're ~stable filter values.
    // axios `timeout` overrides the 30s default per-request (critical for
    // /entities/stats which runs two seq scans over 1.3M rows on cold cache
    // — a warm call is <5ms but the first one after a deploy can take >30s
    // before the pre-warm task in main.py finishes). Hook-level `timeout`
    // is a backstop AbortController abort on top of that.
    const statsQuery = useSerialQueries<EntityStats>([
        { key: 'stats', fn: async (signal) => (await api.get('/entities/stats', { signal, timeout: 60_000 })).data, timeout: 65_000 },
    ], { concurrency: 1, refetchInterval: 60_000 });

    const listQuery = useSerialQueries<Array<{ name: string; count: number }>>([
        { key: 'sources',   fn: async (signal) => (await api.get('/entities/sources', { signal, timeout: 30_000 })).data,        timeout: 35_000 },
        { key: 'types',     fn: async (signal) => (await api.get('/entities/types', { signal, timeout: 30_000 })).data,          timeout: 35_000 },
        { key: 'subcats',   fn: async (signal) => (await api.get('/entities/subcategories', { signal, timeout: 30_000 })).data,  timeout: 35_000 },
        { key: 'countries', fn: async (signal) => (await api.get('/entities/countries', { signal, timeout: 30_000 })).data,      timeout: 35_000 },
    ], { concurrency: 2 });

    const stats = statsQuery.results.stats;
    const sources = useMemo(() => Array.isArray(listQuery.results.sources) ? listQuery.results.sources : [], [listQuery.results.sources]);
    const types = useMemo(() => Array.isArray(listQuery.results.types) ? listQuery.results.types : [], [listQuery.results.types]);
    const subcategories = useMemo(() => Array.isArray(listQuery.results.subcats) ? listQuery.results.subcats : [], [listQuery.results.subcats]);
    const countries = useMemo(() => Array.isArray(listQuery.results.countries) ? listQuery.results.countries : [], [listQuery.results.countries]);

    const statsStatus = statsQuery.statuses.stats;
    const statsError = statsStatus === 'timeout' ? 'Entity stats timed out.'
                     : statsStatus === 'error' ? 'Failed to load entity stats.'
                     : null;
    const sourcesError = listQuery.statuses.sources === 'timeout' ? 'Sources timed out.'
                       : listQuery.statuses.sources === 'error'   ? 'Failed to load sources.' : null;
    const typesError = listQuery.statuses.types === 'timeout' ? 'Categories timed out.'
                     : listQuery.statuses.types === 'error'     ? 'Failed to load categories.' : null;
    const countriesError = listQuery.statuses.countries === 'timeout' ? 'Countries timed out.'
                         : listQuery.statuses.countries === 'error'   ? 'Failed to load countries.' : null;

    // Detail drawer
    const [drawerEntity, setDrawerEntity] = useState<EntityRecord | null>(null);

    // Exchange intelligence — prefetched on mount so accordion expand is instant.
    // Backend pre-warms entity_exchange_brand_counts on boot; cold miss shares
    // a single-flight rollup (can take >30s once) so we wait up to 90s.
    const [exchangeIntel, setExchangeIntel] = useState<ExchangeIntelligence[]>([]);
    const [exchangeIntelOpen, setExchangeIntelOpen] = useState(false);
    const [exchangeIntelLoading, setExchangeIntelLoading] = useState(false);
    const [exchangeIntelError, setExchangeIntelError] = useState<string | null>(null);
    const exchangeIntelAutoOpenedRef = useRef(false);
    const exchangeIntelRetryRef = useRef(0);

    const handleSearchChange = (value: string) => {
        setSearch(value);
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            setPage(0);
            setCommittedSearch(value);
        }, 300);
    };

    useEffect(() => {
        setPage(0);
    }, [filterType, filterSubcategory, filterSource, filterRisk, filterCountry, regionTab, filterRecentActivity]);

    useEffect(() => {
        loadEntities(committedSearch, page);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- loadEntities is recreated each render; deps are the fetch inputs
    }, [filterType, filterSubcategory, filterSource, filterRisk, filterCountry, regionTab, filterRecentActivity, committedSearch, page]);

    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
            abortRef.current?.abort();
        };
    }, []);

    const loadExchangeIntel = async (isRetry = false) => {
        setExchangeIntelLoading(true);
        setExchangeIntelError(null);
        try {
            const data = await entityService.getExchangeIntelligence();
            const rows = Array.isArray(data) ? data : [];
            setExchangeIntel(rows);
            exchangeIntelRetryRef.current = 0;
            // Surface clusters as soon as warm data arrives so officers see
            // them without hunting for the accordion.
            if (rows.length > 0 && !exchangeIntelAutoOpenedRef.current) {
                exchangeIntelAutoOpenedRef.current = true;
                setExchangeIntelOpen(true);
            }
        } catch (error: unknown) {
            const details = error && typeof error === 'object'
                ? error as { code?: string; message?: string }
                : {};
            const isTimeout = details.code === 'ECONNABORTED'
                || String(details.message || '').toLowerCase().includes('timeout');
            // One automatic retry after a cold-miss timeout — startup pre-warm
            // or the first request's single-flight rollup often finishes by then.
            if (isTimeout && !isRetry && exchangeIntelRetryRef.current < 1) {
                exchangeIntelRetryRef.current += 1;
                setExchangeIntelLoading(false);
                await new Promise((r) => setTimeout(r, 1500));
                return loadExchangeIntel(true);
            }
            setExchangeIntelError(
                isTimeout
                    ? 'Exchange intelligence is still warming — retry in a moment.'
                    : 'Failed to load exchange intelligence.',
            );
        } finally {
            setExchangeIntelLoading(false);
        }
    };

    useEffect(() => {
        loadExchangeIntel();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only prefetch
    }, []);

    const loadEntities = async (searchTerm: string = '', pageOverride?: number) => {
        abortRef.current?.abort();
        const ac = new AbortController();
        abortRef.current = ac;
        const reqId = ++requestIdRef.current;
        const currentPage = pageOverride ?? page;
        const hasRows = entities.length > 0;
        if (hasRows) setRefreshing(true);
        else setLoading(true);
        setEntitiesError(null);
        try {
            const params: Record<string, string | number | boolean> = {
                limit: PAGE_SIZE,
                offset: currentPage * PAGE_SIZE,
                sort_by: 'newest',
            };
            if (filterType !== 'all') params.category = filterType;
            if (filterSubcategory !== 'all') params.subcategory = filterSubcategory;
            if (filterSource !== 'all') params.source = filterSource;
            if (filterRisk !== 'all') params.risk_level = filterRisk;
            if (regionTab === 'india') {
                params.country = 'IN';
            } else if (filterCountry !== 'all') {
                params.country = filterCountry;
            }
            if (searchTerm) params.search = searchTerm;
            if (filterRecentActivity) {
                params.has_recent_activity = true;
                params.sort_by = 'recent_activity';
            }

            const data = await entityService.getAll(params, { signal: ac.signal });
            if (reqId !== requestIdRef.current) return;
            if (data && typeof data === 'object' && 'entities' in data) {
                setEntities(Array.isArray(data.entities) ? data.entities : []);
                if (typeof data.total === 'number') {
                    setTotalCount(data.total);
                } else {
                    setTotalCount(null);
                    const countParams = { ...params };
                    delete countParams.limit;
                    delete countParams.offset;
                    delete countParams.sort_by;
                    entityService.getCount(countParams, { signal: ac.signal }).then((countData) => {
                        if (reqId !== requestIdRef.current) return;
                        if (typeof countData?.total === 'number') setTotalCount(countData.total);
                    }).catch(() => { /* pagination chrome only */ });
                }
            } else {
                setEntities(Array.isArray(data) ? data : []);
                setTotalCount(Array.isArray(data) ? data.length : 0);
            }
        } catch (error: unknown) {
            if (ac.signal.aborted) return;
            if (reqId !== requestIdRef.current) return;
            const details = error && typeof error === 'object' ? error as { code?: string; message?: string; name?: string } : {};
            if (details.name === 'CanceledError' || details.code === 'ERR_CANCELED') return;
            const isTimeout = details.code === 'ECONNABORTED' || String(details.message || '').toLowerCase().includes('timeout');
            setEntitiesError(isTimeout ? 'Entity list request timed out.' : 'Failed to load entities.');
            reportClientError(error, { surface: 'entities', endpoint: '/entities' });
        } finally {
            if (reqId === requestIdRef.current) {
                setLoading(false);
                setRefreshing(false);
            }
        }
    };

    const openDrawer = async (entity: EntityRecord) => {
        setDrawerEntity(entity);
        if (!entity.address) return;
        try {
            const full = await entityService.lookup(entity.address, entity.chain);
            setDrawerEntity((prev) => (
                prev && prev.address === entity.address ? { ...prev, ...full } : prev
            ));
        } catch {
            /* keep slim list row */
        }
    };

    const handleRefresh = () => {
        loadEntities(committedSearch);
        statsQuery.refetchAll();
        listQuery.refetchAll();
        loadExchangeIntel();
    };

    const clearFilters = () => {
        setSearch('');
        setCommittedSearch('');
        setFilterType('all');
        setFilterSubcategory('all');
        setFilterSource('all');
        setFilterRisk('all');
        setFilterCountry('all');
        setFilterRecentActivity(false);
        setRegionTab('all');
        setPage(0);
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const totalUnknown = totalCount == null;
    const totalPages = totalUnknown
        ? Math.max(1, page + (entities.length === PAGE_SIZE ? 2 : 1))
        : Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    const firstResult = (totalCount === 0) ? 0 : page * PAGE_SIZE + 1;
    const lastResult = totalUnknown
        ? page * PAGE_SIZE + entities.length
        : Math.min((page + 1) * PAGE_SIZE, totalCount);
    const activeFilterCount = [
        filterType !== 'all',
        filterSubcategory !== 'all',
        filterSource !== 'all',
        filterRisk !== 'all',
        filterCountry !== 'all',
        filterRecentActivity,
        regionTab !== 'all',
    ].filter(Boolean).length;

    const normalizeChain = normalizeChainLabel;

    return (
        <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-5 p-4 sm:p-6 lg:p-8">
            <header className="flex flex-col gap-4 border-b border-border/60 pb-5 lg:flex-row lg:items-end lg:justify-between">
                <div className="max-w-3xl">
                    <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-primary">
                        <ShieldCheck className="h-4 w-4" aria-hidden />
                        Intelligence registry
                    </div>
                    <h1 id="entities-title" className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                        Entities
                    </h1>
                    <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
                        Search verified exchanges, sanctions records, fraud infrastructure, mixers, and other attributed blockchain addresses.
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground" aria-label="Registry coverage">
                        <span><strong className="font-semibold text-foreground">{(stats?.total ?? totalCount ?? 0).toLocaleString()}</strong> entities</span>
                        <span><strong className="font-semibold text-foreground">{stats?.source_count?.toLocaleString?.() ?? '—'}</strong> sources</span>
                        <span><strong className="font-semibold text-foreground">{stats?.by_chain ? Object.keys(stats.by_chain).length : '—'}</strong> chains</span>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={handleRefresh}
                    disabled={loading || refreshing || statsStatus === 'loading'}
                    className={`${brandOutlineBtn} inline-flex min-h-10 items-center justify-center gap-2 self-start disabled:cursor-not-allowed disabled:opacity-50 lg:self-auto`}
                >
                    <RefreshCw className={`h-4 w-4 ${(loading || refreshing || statsStatus === 'loading') ? 'animate-spin' : ''}`} aria-hidden />
                    Refresh registry
                </button>
            </header>

            {statsError && (
                <div className={`${brandStatusWarning} flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-xs`} role="alert">
                    <span className="flex items-center gap-2"><CircleAlert className="h-4 w-4" aria-hidden />{statsError}</span>
                    <button type="button" onClick={() => statsQuery.retry('stats')} className="font-semibold underline underline-offset-2">Retry</button>
                </div>
            )}

            <section aria-labelledby="registry-overview-title">
                <h2 id="registry-overview-title" className="sr-only">Registry overview</h2>
                <div className="relative grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
                    <StatCard label="All entities" value={stats?.total} icon={Database} />
                    <StatCard label="Scam / fraud" value={stats?.scams} tone="danger" icon={AlertTriangle} />
                    <StatCard label="Exchanges" value={stats?.exchanges} tone="brand" icon={Building2} />
                    <StatCard label="Sanctioned" value={stats?.sanctioned} tone="danger" icon={Ban} />
                    <StatCard label="Ransomware" value={stats?.ransomware} tone="warning" icon={Skull} />
                    <StatCard label="Mixers" value={stats?.mixers} tone="warning" icon={Shuffle} />
                    <StatCard label="India-linked" value={stats?.india_linked} tone="brand" icon={MapPin} />
                    <WidgetStatus status={statsStatus} onRetry={() => statsQuery.retry('stats')} label="Stats" />
                </div>
            </section>

            <section className="overflow-hidden rounded-xl border border-border/70 bg-card shadow-sm" aria-labelledby="exchange-intelligence-title">
                <button
                    type="button"
                    onClick={() => setExchangeIntelOpen(!exchangeIntelOpen)}
                    className="flex min-h-14 w-full items-center justify-between gap-3 px-4 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                    aria-expanded={exchangeIntelOpen}
                    aria-controls="exchange-intelligence-content"
                >
                    <span className="flex min-w-0 items-center gap-3">
                        <span className="rounded-lg bg-primary/10 p-2 text-primary"><Building2 className="h-4 w-4" aria-hidden /></span>
                        <span>
                            <span id="exchange-intelligence-title" className="block text-sm font-semibold text-foreground">Exchange intelligence</span>
                            <span className="block text-xs text-muted-foreground">
                                Browse attributed exchange clusters{stats?.exchanges != null ? ` across ${stats.exchanges.toLocaleString()} addresses` : ''}
                            </span>
                        </span>
                    </span>
                    {exchangeIntelOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" aria-hidden /> : <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />}
                </button>
                {exchangeIntelOpen && (
                    <div id="exchange-intelligence-content" className="border-t border-border/60 bg-muted/10 p-4">
                        {exchangeIntelLoading ? (
                            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6" role="status" aria-label="Loading exchange intelligence">
                                {Array.from({ length: 6 }).map((_, i) => (
                                    <div key={i} className="rounded-lg border border-border/70 bg-card p-3">
                                        <SkeletonStat />
                                    </div>
                                ))}
                            </div>
                        ) : exchangeIntelError ? (
                            <div className="flex items-center justify-between gap-3 py-2 text-xs" role="alert">
                                <span className="text-destructive">{exchangeIntelError}</span>
                                <button
                                    type="button"
                                    onClick={() => loadExchangeIntel()}
                                    className="text-primary underline underline-offset-2 hover:text-primary/80"
                                >
                                    Retry
                                </button>
                            </div>
                        ) : exchangeIntel.length === 0 ? (
                            <div className="py-3 text-xs text-muted-foreground">No exchange entities found.</div>
                        ) : (
                            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                                {exchangeIntel.map((ex) => (
                                    <button
                                        type="button"
                                        key={ex.brand}
                                        className="group rounded-lg border border-border/70 bg-card p-3 text-left transition-all duration-200 hover:border-primary/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                                        onClick={() => {
                                            setFilterType('exchange');
                                            setSearch(ex.brand);
                                            setCommittedSearch(ex.brand);
                                            setPage(0);
                                        }}
                                        aria-label={`Filter entities by ${ex.brand}`}
                                    >
                                        <span className="flex items-center gap-1.5">
                                            <span className="truncate text-sm font-semibold text-foreground">{ex.brand}</span>
                                            {ex.india_linked && <span className="rounded bg-primary/10 px-1 text-[9px] font-bold text-primary">IN</span>}
                                            {ex.has_verified && <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" aria-label="Verified" />}
                                        </span>
                                        <span className="mt-2 block text-xl font-bold tabular-nums text-foreground">{ex.address_count.toLocaleString()}</span>
                                        <span className="mt-0.5 block text-[11px] text-muted-foreground">{ex.chain_count} chain{ex.chain_count !== 1 ? 's' : ''}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </section>

            <section className="rounded-xl border border-border/70 bg-card p-4 shadow-sm" aria-labelledby="entity-filters-title">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <SlidersHorizontal className="h-4 w-4 text-primary" aria-hidden />
                        <h2 id="entity-filters-title" className="text-sm font-semibold text-foreground">Find an entity</h2>
                        {activeFilterCount > 0 && (
                            <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">
                                {activeFilterCount} active
                            </span>
                        )}
                    </div>
                    {activeFilterCount > 0 && (
                        <button type="button" onClick={clearFilters} className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground">
                            <RotateCcw className="h-3.5 w-3.5" aria-hidden /> Clear filters
                        </button>
                    )}
                </div>

                <div className="mb-3 flex items-center gap-1 rounded-lg bg-muted/50 p-1 sm:w-fit" role="group" aria-label="Region">
                    <button
                        type="button"
                        onClick={() => setRegionTab('all')}
                        aria-pressed={regionTab === 'all'}
                        className={`min-h-8 flex-1 rounded-md px-3 text-xs font-medium transition-colors sm:flex-none ${
                            regionTab === 'all' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                        }`}
                    >
                        Global registry
                    </button>
                    <button
                        type="button"
                        onClick={() => setRegionTab('india')}
                        aria-pressed={regionTab === 'india'}
                        className={`min-h-8 flex-1 rounded-md px-3 text-xs font-medium transition-colors sm:flex-none ${
                            regionTab === 'india' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                        }`}
                    >
                        India-linked
                    </button>
                </div>

                <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-12">
                    <div className="relative md:col-span-2 xl:col-span-4">
                        <label htmlFor="entity-search" className="sr-only">Search entity name, address, or tag</label>
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                        <input
                            id="entity-search"
                            type="search"
                            placeholder="Search name, address, or tag"
                            className={`min-h-10 w-full rounded-lg border border-input bg-background pl-9 pr-9 text-sm text-foreground placeholder:text-muted-foreground ${brandInputFocus}`}
                            value={search}
                            onChange={(e) => handleSearchChange(e.target.value)}
                        />
                        {search && (
                            <button
                                type="button"
                                onClick={() => handleSearchChange('')}
                                className="absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                                aria-label="Clear search"
                            >
                                <X className="h-4 w-4" aria-hidden />
                            </button>
                        )}
                    </div>
                    <FilterSelect value={filterType} onChange={setFilterType} label="Category" options={types} capitalize className="xl:col-span-2" />
                    <FilterSelect value={filterSubcategory} onChange={setFilterSubcategory} label="Subcategory" options={subcategories} capitalize className="xl:col-span-2" />
                    <FilterSelect value={filterSource} onChange={setFilterSource} label="Source" options={sources} className="xl:col-span-2" />
                    <FilterSelect value={filterCountry} onChange={setFilterCountry} label="Country" options={countries} className="xl:col-span-2" />
                    <label className="xl:col-span-2">
                        <span className="sr-only">Risk level</span>
                        <select
                            className={`min-h-10 w-full rounded-lg border border-input bg-background px-3 text-sm text-foreground ${brandInputFocus}`}
                            value={filterRisk}
                            onChange={(e) => setFilterRisk(e.target.value)}
                        >
                            <option value="all">Risk: All</option>
                            <option value="critical">Risk: Critical</option>
                            <option value="high">Risk: High</option>
                            <option value="medium">Risk: Medium</option>
                            <option value="low">Risk: Low</option>
                        </select>
                    </label>
                    <label className="flex min-h-10 cursor-pointer items-center gap-2 rounded-lg border border-input bg-background px-3 text-xs xl:col-span-2">
                        <input
                            type="checkbox"
                            checked={filterRecentActivity}
                            onChange={(e) => setFilterRecentActivity(e.target.checked)}
                            className="h-4 w-4 rounded border-input accent-primary"
                        />
                        <span className={filterRecentActivity ? 'font-medium text-foreground' : 'text-muted-foreground'}>Recent activity</span>
                    </label>
                </div>
            </section>

            {/* Filter errors */}
            {(sourcesError || typesError || countriesError || entitiesError) && (
                <div className={`${brandStatusWarning} flex flex-wrap items-center gap-3 rounded-lg px-3 py-2 text-xs`} role="alert">
                    {sourcesError && <span>{sourcesError} <button onClick={() => listQuery.retry('sources')} className="text-primary hover:underline">Retry</button></span>}
                    {typesError && <span>{typesError} <button onClick={() => listQuery.retry('types')} className="text-primary hover:underline">Retry</button></span>}
                    {countriesError && <span>{countriesError} <button onClick={() => listQuery.retry('countries')} className="text-primary hover:underline">Retry</button></span>}
                    {entitiesError && <span>{entitiesError} <button onClick={() => loadEntities(committedSearch)} className="text-primary hover:underline">Retry</button></span>}
                </div>
            )}

            <section className="min-h-0 flex-1 overflow-hidden rounded-xl border border-border/70 bg-card shadow-sm" aria-labelledby="entity-results-title">
                <div className="flex flex-col gap-2 border-b border-border/60 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h2 id="entity-results-title" className="text-sm font-semibold text-foreground">Registry results</h2>
                        <p className="mt-0.5 text-xs text-muted-foreground" aria-live="polite">
                            {(loading || refreshing) ? 'Updating results…' : totalUnknown
                                ? `${firstResult.toLocaleString()}–${lastResult.toLocaleString()}+`
                                : `${firstResult.toLocaleString()}–${lastResult.toLocaleString()} of ${totalCount.toLocaleString()}`}
                            {search && !loading ? ` matching “${search}”` : ''}
                        </p>
                    </div>
                    <Pagination page={page} totalPages={totalPages} onPageChange={handlePageChange} compact lastDisabled={totalUnknown} />
                </div>
                <div className="max-h-[calc(100vh-300px)] overflow-auto">
                    <table className="w-full min-w-[760px] text-left text-sm">
                        <caption className="sr-only">Known blockchain entities matching the active search and filters</caption>
                        <thead className="sticky top-0 z-10 border-b border-border bg-muted/95 text-[11px] uppercase tracking-wide text-muted-foreground backdrop-blur">
                            <tr>
                                <th scope="col" className="px-4 py-3 font-semibold">Entity</th>
                                <th scope="col" className="px-3 py-3 font-semibold">Category</th>
                                <th scope="col" className="px-3 py-3 font-semibold">Risk</th>
                                <th scope="col" className="px-3 py-3 font-semibold">Chain</th>
                                <th scope="col" className="px-3 py-3 font-semibold">Source</th>
                                <th scope="col" className="px-3 py-3 font-semibold">Address</th>
                                {filterRecentActivity && <th scope="col" className="px-3 py-3 font-semibold">Last active</th>}
                                <th scope="col" className="w-10 px-2 py-3"><span className="sr-only">Open details</span></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border/60">
                            {loading && entities.length === 0 ? (
                                <tr>
                                    <td colSpan={filterRecentActivity ? 8 : 7} className="p-0">
                                        <SkeletonTable label="Loading entities" />
                                    </td>
                                </tr>
                            ) : entitiesError && entities.length === 0 ? (
                                <tr><td colSpan={filterRecentActivity ? 8 : 7} className="p-10 text-center">
                                    <CircleAlert className="mx-auto h-6 w-6 text-amber-600 dark:text-amber-400" aria-hidden />
                                    <p className="mt-2 text-sm font-medium text-foreground">{entitiesError}</p>
                                    <button type="button" onClick={() => loadEntities(committedSearch)} className="mt-3 text-xs font-semibold text-primary hover:underline">Try again</button>
                                </td></tr>
                            ) : entities.length === 0 ? (
                                <tr><td colSpan={filterRecentActivity ? 8 : 7} className="p-10 text-center">
                                    <Search className="mx-auto h-7 w-7 text-muted-foreground" aria-hidden />
                                    <p className="mt-3 text-sm font-semibold text-foreground">No matching entities</p>
                                    <p className="mx-auto mt-1 max-w-md text-xs leading-5 text-muted-foreground">
                                        Try a broader search or clear the active filters.
                                    </p>
                                    {activeFilterCount > 0 && (
                                        <button type="button" onClick={clearFilters} className="mt-3 text-xs font-semibold text-primary hover:underline">Clear all filters</button>
                                    )}
                                    {search && search.length > 20 && (
                                        <p className="text-muted-foreground/80 text-xs mt-2 max-w-md mx-auto">
                                            This address may still connect to known entities (exchanges, mixers, etc.) via transactions.
                                            <a href={`/workbench?address=${encodeURIComponent(search)}`} className="text-primary hover:underline ml-1">
                                                Open in Workbench
                                            </a>
                                            {' '}to discover connections via transaction analysis.
                                        </p>
                                    )}
                                </td></tr>
                            ) : (
                                entities.map((entity, idx) => (
                                    <tr
                                        key={`${entity.address}-${entity.chain}-${idx}`}
                                        className="group cursor-pointer transition-colors hover:bg-muted/40 focus-within:bg-muted/40"
                                        onClick={() => openDrawer(entity)}
                                    >
                                        {/* Entity name + description + verified */}
                                        <td className="max-w-[320px] px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <CategoryIconTile category={entity.category} />
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-1.5">
                                                        <button
                                                            type="button"
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                openDrawer(entity);
                                                            }}
                                                            className="truncate text-left font-semibold text-foreground hover:text-primary focus-visible:outline-none focus-visible:underline"
                                                        >
                                                            {entity.name || 'Unknown'}
                                                        </button>
                                                        {entity.verified && (
                                                            <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" aria-label="Verified entity" />
                                                        )}
                                                    </div>
                                                    {(entity.description || entity.subcategory) && (
                                                        <div className="text-[11px] text-muted-foreground truncate max-w-[260px]">
                                                            {entity.description
                                                                ? (entity.description.length > 70 ? entity.description.slice(0, 70) + '...' : entity.description)
                                                                : entity.subcategory}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-3 py-3">
                                            <span className="text-xs capitalize text-foreground/80">{entity.category || 'N/A'}</span>
                                        </td>
                                        <td className="px-3 py-3">
                                            <SeverityBadge
                                                level={entity.risk_level}
                                                label={entity.risk_level?.toUpperCase() || 'MEDIUM'}
                                            />
                                        </td>
                                        <td className="px-3 py-3">
                                            <ChainBadge chain={entity.chain} />
                                        </td>
                                        <td className="px-3 py-3">
                                            <SourceMark source={entity.source} className="max-w-[180px]" />
                                        </td>
                                        <td className="px-3 py-3">
                                            <div className="inline-flex items-center gap-0.5">
                                                <button
                                                    type="button"
                                                    className="rounded px-1 py-0.5 font-mono text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(entity.address || '');
                                                        toast.success('Address copied');
                                                    }}
                                                    aria-label={`Copy address ${entity.address || ''}`}
                                                >
                                                    {entity.address ? `${entity.address.substring(0, 8)}...${entity.address.substring(entity.address.length - 6)}` : 'N/A'}
                                                </button>
                                                {entity.address && (
                                                    <CopyIconButton
                                                        value={entity.address}
                                                        onCopy={(value, e) => {
                                                            e.stopPropagation();
                                                            navigator.clipboard.writeText(value);
                                                            toast.success('Address copied');
                                                        }}
                                                        label={`Copy address ${entity.address || ''}`}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                        {filterRecentActivity && (
                                            <td className="px-3 py-3 text-xs">
                                                {entity.last_active_at ? (
                                                    <div>
                                                        <div className="font-medium text-emerald-700 dark:text-emerald-400">{new Date(entity.last_active_at).toLocaleDateString()}</div>
                                                        <div className="text-muted-foreground">{entity.recent_tx_count} txs</div>
                                                    </div>
                                                ) : (
                                                    <span className="text-muted-foreground">—</span>
                                                )}
                                            </td>
                                        )}
                                        <td className="px-2 py-3">
                                            <RowActionButton />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            {totalPages > 1 && (
                <div className="flex justify-center">
                    <Pagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />
                </div>
            )}

            {/* Entity Detail Drawer */}
            {drawerEntity && (
                <EntityDrawer
                    entity={drawerEntity}
                    onClose={() => setDrawerEntity(null)}
                    onInvestigate={() => {
                        navigate(`/workbench?address=${encodeURIComponent(drawerEntity.address || '')}&chain=${normalizeChain(drawerEntity.chain)}`);
                        setDrawerEntity(null);
                    }}
                    normalizeChain={normalizeChain}
                />
            )}
        </div>
    );
};

// ----------------------------------------------------------
// Sub-components
// ----------------------------------------------------------

function StatCard({ label, value, tone = 'neutral', icon }: {
    label: string;
    value?: number;
    tone?: IntelStatTone;
    icon: LucideIcon;
}) {
    return (
        <div className="rounded-xl border border-border/70 bg-card p-3 shadow-sm">
            <div className="flex items-start justify-between gap-2">
                <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
                    <div className="mt-1 text-xl font-bold tabular-nums text-foreground">
                        {value != null ? value.toLocaleString() : '—'}
                    </div>
                </div>
                <IntelStatIcon icon={icon} tone={tone} />
            </div>
        </div>
    );
}

function FilterSelect({ value, onChange, label, options, capitalize, className = '' }: {
    value: string; onChange: (v: string) => void; label: string;
    options: Array<{ name: string; count: number }>; capitalize?: boolean; className?: string;
}) {
    return (
        <label className={className}>
            <span className="sr-only">{label}</span>
            <select
                className={`min-h-10 w-full rounded-lg border border-input bg-background px-3 text-sm text-foreground ${brandInputFocus}`}
                value={value}
                onChange={(e) => onChange(e.target.value)}
            >
                <option value="all">{label}: All</option>
                {options.map((o) => (
                    <option key={o.name} value={o.name}>
                        {capitalize ? o.name.charAt(0).toUpperCase() + o.name.slice(1) : o.name} ({o.count.toLocaleString()})
                    </option>
                ))}
            </select>
        </label>
    );
}

function Pagination({ page, totalPages, onPageChange, compact = false, lastDisabled = false }: {
    page: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    compact?: boolean;
    lastDisabled?: boolean;
}) {
    const buttonClass = 'inline-flex min-h-8 items-center justify-center rounded-md border border-border bg-card text-xs font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40';
    return (
        <nav className="flex items-center gap-1.5" aria-label="Entity results pagination">
            {!compact && (
                <button type="button" onClick={() => onPageChange(0)} disabled={page === 0} className={`${buttonClass} px-2.5`}>
                    First
                </button>
            )}
            <button type="button" onClick={() => onPageChange(page - 1)} disabled={page === 0} className={`${buttonClass} w-8`} aria-label="Previous page">
                <ChevronLeft className="h-4 w-4" aria-hidden />
            </button>
            <span className="min-w-[88px] px-1 text-center text-xs tabular-nums text-muted-foreground">
                {page + 1} / {totalPages.toLocaleString()}
            </span>
            <button type="button" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages - 1} className={`${buttonClass} w-8`} aria-label="Next page">
                <ChevronRight className="h-4 w-4" aria-hidden />
            </button>
            {!compact && (
                <button type="button" onClick={() => onPageChange(totalPages - 1)} disabled={lastDisabled || page >= totalPages - 1} className={`${buttonClass} px-2.5`}>
                    Last
                </button>
            )}
        </nav>
    );
}

function EntityDrawer({ entity, onClose, onInvestigate, normalizeChain }: {
    entity: EntityRecord; onClose: () => void; onInvestigate: () => void;
    normalizeChain: (c?: string | null) => string;
}) {
    const navigate = useNavigate();
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const drawerRef = useRef<HTMLElement>(null);
    const tags = Array.isArray(entity.tags) ? entity.tags : [];
    const nodeLabels = Array.isArray(entity.node_labels) ? entity.node_labels : [];
    const attributions = Array.isArray(entity.intelligence_attributions) ? entity.intelligence_attributions : [];
    const metadata = entity.metadata && typeof entity.metadata === 'object' ? entity.metadata : {};

    // Fetch recent transactions on demand
    const [txs, setTxs] = useState<EntityTransaction[]>([]);
    const [txsLoading, setTxsLoading] = useState(false);
    const [txsLoaded, setTxsLoaded] = useState(false);

    const loadTransactions = useCallback(async () => {
        if (txsLoaded || txsLoading || !entity.address || !entity.chain) return;
        setTxsLoading(true);
        try {
            const chain = normalizeChain(entity.chain);
            const { data } = await api.get(`/addresses/${chain}/${entity.address}/transactions`, {
                params: { limit: 20, offset: 0 },
            });
            setTxs(data?.transactions || []);
        } catch {
            // silent — transactions are optional
        } finally {
            setTxsLoading(false);
            setTxsLoaded(true);
        }
    }, [entity.address, entity.chain, txsLoaded, txsLoading, normalizeChain]);

    // Auto-load if entity has recent activity
    useEffect(() => {
        if ((entity.recent_tx_count ?? 0) > 0) loadTransactions();
    }, [entity.recent_tx_count, loadTransactions]);

    useEffect(() => {
        const previousOverflow = document.body.style.overflow;
        const previouslyFocused = document.activeElement as HTMLElement | null;
        document.body.style.overflow = 'hidden';
        closeButtonRef.current?.focus();
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
            if (event.key !== 'Tab') return;

            const focusable = drawerRef.current?.querySelectorAll<HTMLElement>(
                'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
            );
            if (!focusable?.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', handleKeyDown);
            previouslyFocused?.focus();
        };
    }, [onClose]);

    return (
        <>
            <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-[1px]" onClick={onClose} aria-hidden />
            <aside
                ref={drawerRef}
                className="fixed right-0 top-0 z-50 h-full w-[520px] max-w-[94vw] overflow-y-auto border-l border-border bg-card shadow-2xl"
                role="dialog"
                aria-modal="true"
                aria-labelledby="entity-drawer-title"
                aria-describedby={entity.description ? 'entity-drawer-description' : undefined}
            >
                {/* Header */}
                <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-border bg-card/95 p-5 backdrop-blur">
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                            <CategoryIconTile category={entity.category} />
                            <h2 id="entity-drawer-title" className="truncate text-lg font-bold text-foreground">{entity.name || 'Unknown'}</h2>
                            {entity.verified && <BadgeCheck className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" aria-label="Verified entity" />}
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            <SeverityBadge
                                level={entity.risk_level}
                                label={entity.risk_level?.toUpperCase() || 'MEDIUM'}
                            />
                            <span className="capitalize">{entity.category}</span>
                            {entity.subcategory && <span>&middot; {entity.subcategory}</span>}
                            <ChainBadge chain={entity.chain} />
                        </div>
                    </div>
                    <button
                        ref={closeButtonRef}
                        type="button"
                        onClick={onClose}
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        aria-label="Close entity details"
                    >
                        <X className="h-5 w-5" aria-hidden />
                    </button>
                </div>

                <div className="space-y-6 p-5">
                    {/* Actions */}
                    <div className="flex flex-col gap-2 sm:flex-row">
                        <button
                            type="button"
                            onClick={onInvestigate}
                            className={`${brandPrimaryBtnMd} flex min-h-10 flex-1 items-center justify-center gap-2`}
                        >
                            <ExternalLink className="h-4 w-4" aria-hidden />
                            Investigate in Workbench
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                navigator.clipboard.writeText(entity.address || '');
                                toast.success('Address copied');
                            }}
                            className={`${brandOutlineBtn} inline-flex min-h-10 items-center justify-center gap-2`}
                        >
                            <Copy className="h-4 w-4" aria-hidden /> Copy address
                        </button>
                    </div>

                    {/* Address */}
                    <DrawerSection title="Address">
                        <code className="text-xs font-mono text-foreground/80 break-all select-all">{entity.address}</code>
                    </DrawerSection>

                    {/* Description */}
                    {entity.description && (
                        <DrawerSection title="Description">
                            <p id="entity-drawer-description" className="text-sm leading-6 text-foreground/80">{entity.description}</p>
                        </DrawerSection>
                    )}

                    {/* Key Fields */}
                    <DrawerSection title="Details">
                        <div className="grid grid-cols-2 gap-4 rounded-lg border border-border/60 bg-muted/20 p-3 text-xs">
                            <Field label="Brand" value={entity.brand || '—'} />
                            <Field label="Wallet Role" value={entity.wallet_role || '—'} />
                            <Field label="Source" value={<SourceMark source={entity.source} />} />
                            <Field label="Confidence" value={entity.confidence != null ? `${(entity.confidence * 100).toFixed(0)}%` : '—'} />
                            <Field label="Country" value={entity.country || '—'} />
                            <Field label="Verified" value={entity.verified ? 'Yes' : 'No'} highlight={entity.verified} />
                        </div>
                    </DrawerSection>

                    {/* Tags */}
                    {tags.length > 0 && (
                        <DrawerSection title={`Tags (${tags.length})`}>
                            <div className="flex flex-wrap gap-1">
                                {tags.map((t: string, i: number) => (
                                    <span key={i} className="px-2 py-0.5 bg-muted text-foreground/80 rounded text-[11px]">{t}</span>
                                ))}
                            </div>
                        </DrawerSection>
                    )}

                    {/* Node Labels */}
                    {nodeLabels.length > 0 && (
                        <DrawerSection title={`Node Labels (${nodeLabels.length})`}>
                            <div className="flex flex-wrap gap-1">
                                {nodeLabels.map((l: string, i: number) => (
                                    <span key={i} className="px-2 py-0.5 bg-muted text-foreground rounded text-[11px]">{l}</span>
                                ))}
                            </div>
                        </DrawerSection>
                    )}

                    {/* Intelligence Attributions */}
                    {attributions.length > 0 && (
                        <DrawerSection title={`Intelligence Attributions (${attributions.length})`}>
                            <div className="space-y-2">
                                {attributions.map((a: EntityAttribution, i: number) => (
                                    <div key={i} className="bg-secondary/30 rounded p-2 text-xs border border-border">
                                        <div className="flex justify-between items-start">
                                            <span className="text-muted-foreground">{a.key}</span>
                                            {a.confidence != null && (
                                                <span className="text-[10px] text-muted-foreground">{(a.confidence * 100).toFixed(0)}% conf</span>
                                            )}
                                        </div>
                                        <div className="text-foreground font-medium mt-0.5">{a.value}</div>
                                        {a.source && <div className="text-muted-foreground mt-0.5">Source: {a.source}</div>}
                                        {a.evidence && <div className="text-muted-foreground mt-0.5 truncate" title={a.evidence}>Evidence: {a.evidence}</div>}
                                    </div>
                                ))}
                            </div>
                        </DrawerSection>
                    )}

                    {/* Metadata */}
                    {Object.keys(metadata).length > 0 && (
                        <DrawerSection title="Metadata">
                            <div className="bg-secondary/30 rounded p-2 text-xs border border-border">
                                <pre className="text-muted-foreground whitespace-pre-wrap break-all max-h-[200px] overflow-y-auto">
                                    {JSON.stringify(metadata, null, 2)}
                                </pre>
                            </div>
                        </DrawerSection>
                    )}

                    {/* Recent Transactions */}
                    <DrawerSection title={`Recent Transactions${txs.length > 0 ? ` (${txs.length})` : ''}`}>
                        {!txsLoaded && !txsLoading && (
                            <button
                                onClick={loadTransactions}
                                className="text-xs text-primary hover:text-primary/80 underline"
                            >
                                Load transactions
                            </button>
                        )}
                        {txsLoading && <div className="text-xs text-muted-foreground">Loading...</div>}
                        {txsLoaded && txs.length === 0 && (
                            <div className="text-xs text-muted-foreground">No recent transactions found</div>
                        )}
                        {txs.length > 0 && (
                            <div className="space-y-1 max-h-[300px] overflow-y-auto styled-scrollbar">
                                {txs.map((tx: EntityTransaction, i: number) => {
                                    const isSender = addressesMatch(tx.from_address, entity.address, entity.chain);
                                    const counterparty = isSender ? tx.to_address : tx.from_address;
                                    const cpEntity = tx.other_address_entity_name;
                                    const val = tx.value || 0;
                                    const usd = tx.historical_usd;
                                    const token = tx.token_symbol || '';
                                    const ts = tx.timestamp;
                                    const fmtDate = ts ? (() => {
                                        const d = new Date(typeof ts === 'number' ? (ts > 1e12 ? ts : ts * 1000) : ts);
                                        return isNaN(d.getTime()) ? '' : d.toLocaleDateString();
                                    })() : '';
                                    return (
                                        <button
                                            type="button"
                                            key={tx.tx_hash || i}
                                            className="group flex w-full min-h-9 items-center gap-2 rounded border border-border/50 px-2 py-1.5 text-left text-[10px] transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                                            onClick={() => {
                                                navigate(`/workbench?address=${encodeURIComponent(entity.address || '')}&chain=${normalizeChain(entity.chain)}`);
                                                onClose();
                                            }}
                                            aria-label={`Open transaction with ${cpEntity || counterparty || 'unknown counterparty'} in Workbench`}
                                        >
                                            <span className={`shrink-0 ${isSender ? 'text-destructive' : 'text-emerald-700 dark:text-emerald-400'}`}>
                                                {isSender ? '↑' : '↓'}
                                            </span>
                                            <span className="text-muted-foreground shrink-0 w-[65px]">{fmtDate}</span>
                                            <span className="truncate flex-1 font-mono" title={counterparty ?? ''}>
                                                {cpEntity || (counterparty ? `${counterparty.slice(0, 8)}...${counterparty.slice(-4)}` : '?')}
                                            </span>
                                            <span className="shrink-0 tabular-nums font-medium text-right">
                                                {val >= 1000 ? `${(val / 1000).toFixed(1)}K` : val.toFixed(val < 1 ? 4 : 2)} {token}
                                            </span>
                                            {usd != null && usd > 0 && (
                                                <span className="shrink-0 text-muted-foreground tabular-nums">${usd >= 1000 ? `${(usd / 1000).toFixed(1)}K` : usd.toFixed(2)}</span>
                                            )}
                                            <ExternalLink className="w-3 h-3 text-muted-foreground/40 group-hover:text-primary shrink-0" />
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </DrawerSection>
                </div>
            </aside>
        </>
    );
}

function DrawerSection({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">{title}</h3>
            {children}
        </div>
    );
}

function Field({ label, value, highlight }: { label: string; value: React.ReactNode; highlight?: boolean }) {
    return (
        <div>
            <div className="text-[10px] text-muted-foreground uppercase">{label}</div>
            <div className={`text-foreground/80 ${highlight ? 'text-emerald-400 font-medium' : ''}`}>{value}</div>
        </div>
    );
}
